import { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";

type SelectProps = {
  data: { [key: string]: string | number }[];
  selected: { [key: string]: string | number } | string | null ;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  width?: string;
  onChange: (value: { [key: string]: string | number }) => void;
}

function Select({ data, selected, placeholder, label, required, onChange, disabled = false, width = '300px' }: SelectProps) {
  return (
    <Listbox value={selected} onChange={onChange} disabled={disabled}>
      <div className="relative">
        {label &&
          <div className='flex justify-between items-center text-black text-base mb-1'>
            {label}
            {required && <span className="text-[#BA0C2F]">*</span>}
          </div>}

        <Listbox.Button  className={`relative w-full flex gap-2 justify-between items-center text-base rounded-md border py-3 px-5 outline-none shadow-custom ${
            disabled
              ? "bg-[#E5E7EB] text-[#6B7280] border-[#DFE4EA] cursor-not-allowed"
              : "bg-white text-black border-[#DFE4EA]"
          }`} style={{width: width}} >
        <span className="block truncate" style={{maxWidth: width}}>
          {typeof selected === 'object' && selected !== null && 'name' in selected
            ? <span>{selected.name}</span>
            : <span className="text-gray-400">{placeholder}</span>}
        </span>

        <span className="pointer-events-none">
          <ChevronDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-[101] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none">
            {data.map((el) => {
              const isOptionDisabled = el.disabled === true || el.available === false;
              return (
                <Listbox.Option
                  key={el.id}
                  value={el}
                  disabled={isOptionDisabled}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-3 pr-9 ${
                      isOptionDisabled
                        ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                        : active ? 'bg-darkblue text-white' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected, active }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {el.name}
                        </span>
                        {selected && !isOptionDisabled ? (
                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                </Listbox.Option>
              );
            })}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

export default Select;
